import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BadgeCheck, Search, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export default function Verify() {
  const { certId } = useParams();
  const [input, setInput] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async (id) => {
    if (!id.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/verify/${encodeURIComponent(id.trim().toUpperCase())}`);
      setResult(data);
    } catch {
      setResult({ verified: false, message: "INVALID / NOT FOUND" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (certId) verify(certId);
  }, [certId]);

  return (
    <div className="bg-navy-base min-h-screen" data-testid="verify-page">
      <SEO title="Verify Certificate | XTM TECHNOLOGIES Jorhat"
        description="Verify the authenticity of an XTM TECHNOLOGIES FutureSkills Academy certificate using its Certificate ID." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Certificate Verification</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4" data-testid="verify-heading">Verify Certificate</h1>
          <p className="text-base text-slate-300 mb-8">Enter the Certificate ID printed on the certificate or scan its QR code (e.g. XTM-CERT-2026-000123).</p>
          <form onSubmit={(e) => { e.preventDefault(); verify(input); }} className="glass rounded-2xl p-2 flex items-center gap-2">
            <Search className="w-5 h-5 text-orange-400 ml-3 shrink-0" />
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="XTM-CERT-2026-000000"
              className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none text-sm py-3 font-mono" data-testid="verify-input" />
            <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-colors" data-testid="verify-submit-btn">
              {loading ? "Checking…" : "Verify"}
            </Button>
          </form>

          {result && (
            <div className={`mt-8 rounded-2xl border p-7 ${result.verified ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="verify-result">
              {result.verified ? (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <BadgeCheck className="w-8 h-8 text-emerald-400" />
                    <span className="font-display text-2xl font-extrabold text-emerald-400 tracking-wide" data-testid="verify-status">VERIFIED</span>
                  </div>
                  <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                    {[["Student Name", result.certificate.student_name], ["Course", result.certificate.course],
                      ["Completion Date", result.certificate.completion_date], ["Certificate ID", result.certificate.certificate_id],
                      ["Issuing Organization", result.certificate.issuing_organization], ["Status", "Valid"]].map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt>
                        <dd className="text-white font-medium mt-0.5">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  {result.certificate.demo && <p className="text-xs text-slate-500 mt-5">This is a demo certificate record shown for demonstration purposes.</p>}
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-red-400" />
                  <div>
                    <span className="font-display text-2xl font-extrabold text-red-400 tracking-wide" data-testid="verify-status-invalid">INVALID / NOT FOUND</span>
                    <p className="text-sm text-slate-400 mt-1">No certificate matches this ID. Check the ID and try again, or contact info@xtmtechnologies.in.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
